import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {

  /**
   * Strong password validator matching backend @IsStrongPassword
   * Minimum 8 characters, uppercase, lowercase, number, special character
   */
  static strongPassword(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      if (!value) {
        return null; // Let required validator handle empty values
      }

      if (typeof value !== 'string') {
        return { strongPassword: { message: 'Password must be a string' } };
      }

      // Minimum 8 characters
      if (value.length < 8) {
        return { strongPassword: { message: 'Password must be at least 8 characters long' } };
      }

      // Must contain at least one lowercase letter
      if (!/[a-z]/.test(value)) {
        return { strongPassword: { message: 'Password must contain at least one lowercase letter' } };
      }

      // Must contain at least one uppercase letter
      if (!/[A-Z]/.test(value)) {
        return { strongPassword: { message: 'Password must contain at least one uppercase letter' } };
      }

      // Must contain at least one number
      if (!/\d/.test(value)) {
        return { strongPassword: { message: 'Password must contain at least one number' } };
      }

      // Must contain at least one special character
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
        return { strongPassword: { message: 'Password must contain at least one special character' } };
      }

      return null;
    };
  }

  /**
   * Phone number validator matching backend @IsPhoneNumber
   * Validates international phone number formats
   */
  static phoneNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      if (!value) {
        return null; // Allow empty for optional fields
      }

      if (typeof value !== 'string') {
        return { phoneNumber: { message: 'Phone number must be a string' } };
      }

      // Basic international phone number regex
      const phoneRegex = /^(\+)?[1-9]\d{1,14}$/;
      const cleanPhone = value.replace(/[\s\-\(\)]/g, '');
      
      if (!phoneRegex.test(cleanPhone)) {
        return { phoneNumber: { message: 'Phone number must be a valid international format' } };
      }

      return null;
    };
  }

  /**
   * Valid name validator matching backend @IsValidName
   * Ensures names contain only valid characters
   */
  static validName(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      if (!value) {
        return null; // Allow empty for optional fields
      }

      if (typeof value !== 'string') {
        return { validName: { message: 'Name must be a string' } };
      }

      // Allow letters, numbers, spaces, hyphens, apostrophes, periods, and underscores
      const nameRegex = /^[a-zA-Z0-9\s\-'._]+$/;
      if (!nameRegex.test(value.trim()) || value.trim().length === 0) {
        return { validName: { message: 'Name must contain only letters, numbers, spaces, hyphens, apostrophes, periods, and underscores' } };
      }

      return null;
    };
  }

  /**
   * ObjectId validator matching backend @IsObjectId
   * Validates MongoDB ObjectId format
   */
  static objectId(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      if (!value) {
        return null; // Allow empty for optional fields
      }

      if (typeof value !== 'string') {
        return { objectId: { message: 'ObjectId must be a string' } };
      }

      // MongoDB ObjectId regex pattern
      const objectIdRegex = /^[0-9a-fA-F]{24}$/;
      if (!objectIdRegex.test(value)) {
        return { objectId: { message: 'Invalid ObjectId format' } };
      }

      return null;
    };
  }

  /**
   * File size validator matching backend @IsFileSizeValid
   * Validates file size in bytes with human-readable limits
   */
  static fileSize(maxSizeMB: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const file = control.value;
      
      if (!file) {
        return null; // Allow empty for optional fields
      }

      if (!(file instanceof File)) {
        return { fileSize: { message: 'Value must be a File object' } };
      }

      const maxSizeBytes = maxSizeMB * 1024 * 1024; // Convert MB to bytes
      if (file.size > maxSizeBytes) {
        return { fileSize: { message: `File size must not exceed ${maxSizeMB}MB` } };
      }

      return null;
    };
  }

  /**
   * File type validator for CAD files
   * Validates that uploaded files are supported CAD formats (STEP, STP)
   */
  static fileType(allowedTypes: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const file = control.value;
      
      if (!file) {
        return null; // Allow empty for optional fields
      }

      if (!(file instanceof File)) {
        return { fileType: { message: 'Value must be a File object' } };
      }

      // Get file extension
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      
      // Check if file extension is in allowed types
      const normalizedAllowedTypes = allowedTypes.map(type => 
        type.startsWith('.') ? type.toLowerCase() : `.${type.toLowerCase()}`
      );

      if (!normalizedAllowedTypes.includes(fileExtension)) {
        const formatList = allowedTypes.join(', ').toUpperCase();
        return { fileType: { message: `Only ${formatList} files are supported` } };
      }

      return null;
    };
  }

  /**
   * URL validator matching backend @IsValidURL
   * Validates HTTP/HTTPS URLs
   */
  static validUrl(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      if (!value) {
        return null; // Allow empty for optional fields
      }

      if (typeof value !== 'string') {
        return { validUrl: { message: 'URL must be a string' } };
      }

      try {
        const url = new URL(value);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
          return { validUrl: { message: 'URL must be a valid HTTP or HTTPS URL' } };
        }
      } catch {
        return { validUrl: { message: 'URL must be a valid HTTP or HTTPS URL' } };
      }

      return null;
    };
  }

  /**
   * Password match validator for confirm password fields
   * Compares password and confirm password fields
   */
  static passwordMatch(passwordField: string, confirmPasswordField: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.get(passwordField);
      const confirmPassword = control.get(confirmPasswordField);

      if (!password || !confirmPassword) {
        return null;
      }

      if (password.value !== confirmPassword.value) {
        return { passwordMismatch: { message: 'Passwords do not match' } };
      }

      return null;
    };
  }

  /**
   * Custom email validator with better error message
   * Enhanced version of Angular's email validator
   */
  static email(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      if (!value) {
        return null; // Let required validator handle empty values
      }

      if (typeof value !== 'string') {
        return { email: { message: 'Email must be a string' } };
      }

      // Enhanced email regex
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(value)) {
        return { email: { message: 'Please provide a valid email address' } };
      }

      return null;
    };
  }

  /**
   * Custom required validator with better error message
   */
  static required(fieldName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      if (value === null || value === undefined || value === '' || 
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'string' && value.trim() === '')) {
        return { required: { message: `${fieldName} is required` } };
      }

      return null;
    };
  }

  /**
   * Min length validator with custom error message
   */
  static minLength(minLength: number, fieldName?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      if (!value) {
        return null; // Let required validator handle empty values
      }

      if (typeof value !== 'string') {
        return { minLength: { message: `${fieldName || 'Field'} must be a string` } };
      }

      if (value.length < minLength) {
        return { minLength: { message: `${fieldName || 'Field'} must be at least ${minLength} characters long` } };
      }

      return null;
    };
  }

  /**
   * Max length validator with custom error message
   */
  static maxLength(maxLength: number, fieldName?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      if (!value) {
        return null;
      }

      if (typeof value !== 'string') {
        return { maxLength: { message: `${fieldName || 'Field'} must be a string` } };
      }

      if (value.length > maxLength) {
        return { maxLength: { message: `${fieldName || 'Field'} is too long. Maximum length is ${maxLength} characters` } };
      }

      return null;
    };
  }

  /**
   * Get error message from validation errors
   * Extracts the first error message from ValidationErrors
   */
  static getErrorMessage(errors: ValidationErrors): string {
    if (!errors) {
      return '';
    }

    // Return the first error message found
    for (const key in errors) {
      if (errors[key] && errors[key].message) {
        return errors[key].message;
      }
    }

    // Fallback error messages for standard validators
    if (errors['required']) return 'This field is required';
    if (errors['email']) return 'Please enter a valid email address';
    if (errors['minlength']) return `Minimum length is ${errors['minlength'].requiredLength} characters`;
    if (errors['maxlength']) return `Maximum length is ${errors['maxlength'].requiredLength} characters`;
    if (errors['pattern']) return 'Invalid format';

    return 'Invalid input';
  }

  /**
   * Check if form control has errors and is touched
   */
  static hasError(control: AbstractControl | null): boolean {
    return !!(control && control.invalid && control.touched);
  }

  /**
   * Get the error message for a form control
   */
  static getControlErrorMessage(control: AbstractControl | null): string {
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    return this.getErrorMessage(control.errors);
  }
}
